import { Link } from "react-router-dom";
import { useEffect } from "react";
import { getAllPodcasts } from "./PodcastLandingPage";

export default function TranscriptsIndexPage() {
  const podcasts = getAllPodcasts();

  useEffect(() => {
    document.title = "Podcast Transcripts - Echonour";
    
    // JSON-LD
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Podcast Transcripts",
      "description": "Get accurate transcripts of your favorite podcasts with speaker identification and timestamps.",
      "url": "https://echonour.com/transcripts",
      "hasPart": podcasts.map(p => ({
        "@type": "WebPage",
        "name": `${p.name} Transcripts`,
        "url": `https://echonour.com/transcripts/${p.slug}`
      }))
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonld);
    script.id = 'transcripts-jsonld';
    document.head.appendChild(script);
    
    return () => {
      const existing = document.getElementById('transcripts-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-[calc(100vh-68px)] bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
            Podcast Transcripts
          </h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            Get accurate transcripts of your favorite podcasts with speaker identification and timestamps
          </p>
        </div>

        {/* Podcast Grid */}
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {podcasts.map((podcast) => (
            <Link
              key={podcast.slug}
              to={`/transcripts/${podcast.slug}`}
              className="group rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-blue-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-700"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-xl font-bold text-white">
                  {podcast.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-gray-900 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
                    {podcast.name}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {podcast.host}
                  </p>
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    {podcast.category}
                  </p>
                </div>
              </div>
              <p className="mt-4 text-sm text-gray-600 line-clamp-2 dark:text-gray-400">
                {podcast.description}
              </p>
              <div className="mt-4 flex items-center text-sm font-medium text-blue-600 dark:text-blue-400">
                Transcribe episodes
                <svg className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-xl border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Don't see your podcast?
          </h3>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            You can transcribe any podcast by pasting a YouTube URL or audio link
          </p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700"
          >
            Transcribe Any URL
            <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {/* Disclaimer */}
        <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
          Echonour is an independent transcription service. We are not affiliated with any of the podcasts listed above.
        </p>
      </div>
    </div>
  );
}
